/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Text, Box, Heading} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'

import {useGlobalSearch} from '../../../contexts/global-elements-context'
import {generateStyles} from '../../../components/optimizely/utils/utils'
import Seo from '../../../components/seo'

const EmptySearchResultsTitle = ({hasSelectedFilters}) => {
    const intl = useIntl()
    const {data: globalSearchData, isLoading: isSearchLoading} = useGlobalSearch()

    // Extract search empty messages from global data
    const emptyMessage = globalSearchData?.emptyMessage
    const {formatMessage} = useIntl()

    const searchResultsPageTitle = formatMessage({
        id: 'search.results.page.title',
        defaultMessage: 'Search | Garage Clothing'
    })

    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)
    let searchQuery = urlParams.get('q')

    // Convert Optimizely text styles to Chakra UI styles
    const searchResultMessageStyle = emptyMessage?.searchResultMessageStyle
        ? generateStyles(emptyMessage.searchResultMessageStyle)
        : {}
    return (
        <Box data-testid="sf-product-empty-list-page-title" px={{base: 2, lg: 0}}>
            {searchQuery && <Seo title={searchResultsPageTitle} />}
            <Heading
                as="h2"
                fontSize={{base: 16, lg: 24}}
                textTransform="uppercase"
                fontWeight="bold"
                mt={{base: 12, lg: 16}}
                mb={{base: 2, lg: 4}}
            >
                {hasSelectedFilters ? (
                    <FormattedMessage defaultMessage="No exact matches" id="search.result.no_exact_matches" />
                ) : (
                    <FormattedMessage defaultMessage="SEARCH RESULT" id="search.result.heading" />
                )}
            </Heading>
            <Text mb={{base: 12, lg: 16}} {...searchResultMessageStyle}>
                {hasSelectedFilters
                    ? intl.formatMessage({
                          id: 'empty_search_results.info.try_changing_filters',
                          defaultMessage:
                              'Try changing or removing some of your filters or adjusting your search area.'
                      })
                    : emptyMessage?.searchResultMessage ||
                      intl.formatMessage({
                          id: 'empty_search_results.info.sorry_no_items',
                          defaultMessage:
                              'Sorry! No items found. But here are some recommendations for you.'
                      })}
            </Text>
        </Box>
    )
}

EmptySearchResultsTitle.propTypes = {
    hasSelectedFilters: PropTypes.bool
}

export default EmptySearchResultsTitle
